import { UserRole } from '@types';
import { SUBSCRIPTION_TIERS, formatStorageSize, getStoragePercentage } from '@config/subscription';
import { supabase } from '@config/supabase';

export interface StorageUsage {
  used: number; // bytes
  limit: number; // bytes
  percentage: number;
  remaining: number; // bytes
  fileCount: number;
  maxFiles: number;
}

export interface UploadValidation {
  canUpload: boolean;
  reason?: 'storage_limit' | 'file_limit';
  message?: string;
  storageUsage?: StorageUsage;
}

class StorageLimitService {
  // Cache for storage usage to reduce API calls
  private usageCache = new Map<string, { data: StorageUsage; timestamp: number }>();
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes cache

  /**
   * Get the current storage usage for a user
   * Uses caching and optimized queries for better performance
   */
  async getStorageUsage(userId: string, userRole: UserRole, files: { size: number }[] = []): Promise<StorageUsage> {
    const tier = SUBSCRIPTION_TIERS[userRole];
    if (!tier) {
      throw new Error(`Invalid user role: ${userRole}`);
    }
    
    // Check cache first
    const cached = this.usageCache.get(userId);
    const now = Date.now();
    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      console.log('📦 [storage-limit] Using cached storage usage');
      return cached.data;
    }
    
    const fallbackUsed = files.reduce((total, file) => total + file.size, 0);
    let used = fallbackUsed;
    let limit = tier.storageLimit;
    let fileCount = files.length;
    const maxFiles = tier.maxFiles;

    // Optimize: Fetch profile and file count in parallel for better performance
    const [profileResult, fileCountResult] = await Promise.allSettled([
      // Fetch profile data
      supabase
        .from('profiles')
        .select('storage_used, storage_limit, storage_addons_total_bytes')
        .eq('id', userId)
        .single(),
      // Fetch file count
      supabase
        .from('files')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('is_trashed', false)
    ]);

    // Process profile result
    if (profileResult.status === 'fulfilled') {
      const { data, error } = profileResult.value;
      if (error) {
        console.warn('[storage-limit] Failed to load profile usage from server', error);
      } else if (data) {
        if (typeof data.storage_used === 'number') {
          used = data.storage_used;
        }

        const storageAddons =
          typeof data.storage_addons_total_bytes === 'number' ? data.storage_addons_total_bytes : 0;
        const profileLimit = typeof data.storage_limit === 'number' ? data.storage_limit : null;
        const baseLimit = profileLimit !== null && profileLimit > 0
          ? Math.max(profileLimit, tier.storageLimit)
          : tier.storageLimit;

        if (storageAddons > 0 && baseLimit <= tier.storageLimit) {
          limit = baseLimit + storageAddons;
        } else {
          limit = baseLimit;
        }
      }
    } else {
      console.warn('[storage-limit] Unexpected error while loading profile usage', profileResult.reason);
    }

    // Process file count result
    if (fileCountResult.status === 'fulfilled') {
      const { count, error } = fileCountResult.value;
      if (error) {
        console.warn('[storage-limit] Failed to count files on server', error);
      } else if (typeof count === 'number') {
        fileCount = count;
      }
    } else {
      console.warn('[storage-limit] Unexpected error while counting files', fileCountResult.reason);
    }

    const safeLimit = limit > 0 ? limit : tier.storageLimit;
    const percentage = safeLimit > 0 ? getStoragePercentage(used, safeLimit) : 100;
    const remaining = Math.max(0, safeLimit - used);
    
    const result: StorageUsage = {
      used,
      limit: safeLimit,
      percentage,
      remaining,
      fileCount,
      maxFiles,
    };

    // Update cache
    this.usageCache.set(userId, { data: result, timestamp: now });
    
    return result;
  }

  /**
   * Clear cache for a specific user (call after uploads/deletions)
   */
  clearCache(userId: string): void {
    this.usageCache.delete(userId);
  }

  /**
   * Clear all cache
   */
  clearAllCache(): void {
    this.usageCache.clear();
  }

  /**
   * Check if a user can upload new files
   * IMPORTANT: During downgrade warning (3 days), uploads are allowed
   * Only block during normal overflow (21 days with restrictions)
   */
  async canUpload(
    userId: string,
    userRole: UserRole,
    currentFiles: { size: number }[],
    newFiles: { size: number }[]
  ): Promise<UploadValidation> {
    const tier = SUBSCRIPTION_TIERS[userRole];
    const storageUsage = await this.getStorageUsage(userId, userRole, currentFiles);
    
    // Calculate total storage after upload
    const newFilesSize = newFiles.reduce((total, file) => total + file.size, 0);
    const totalUsed = storageUsage.used + newFilesSize;
    const effectiveLimit = storageUsage.limit;
    
    // Check file count limit first
    const totalFiles = storageUsage.fileCount + newFiles.length;
    if (totalFiles > tier.maxFiles) {
      return {
        canUpload: false,
        reason: 'file_limit',
        message: `File limit reached. You can upload up to ${tier.maxFiles} files on the ${tier.name} plan.`,
        storageUsage,
      };
    }
    
    // Check if upload would exceed storage limit
    // IMPORTANT: Only block if user is ALREADY in overflow state, not if upload would cause overflow
    // This allows users to upload up to the limit, and only blocks if they're already over
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('downgrade_warning_started_at, overflow_state, storage_used, storage_limit')
        .eq('id', userId)
        .single();

      if (profile) {
        const hasDowngradeWarning = !!profile.downgrade_warning_started_at;
        const storageUsed = profile.storage_used || 0;
        const storageLimit = profile.storage_limit || 0;
        const isActualOverflow = storageUsed > storageLimit;
        const isNormalOverflow = profile.overflow_state && profile.overflow_state !== 'none';

        // If in downgrade warning period, allow uploads (user keeps all functions for 3 days)
        if (hasDowngradeWarning) {
          // During downgrade warning, allow uploads even if over limit
          // User has 3 days to delete files or upgrade before suspension
          return {
            canUpload: true,
            storageUsage,
          };
        }

        // Only block if user is ALREADY in overflow state (not just if upload would cause overflow)
        // This allows users to upload up to the limit, and only blocks if they're already over
        if (isActualOverflow && isNormalOverflow) {
          const overflowAmount = storageUsed - storageLimit;
          const needed = totalUsed > effectiveLimit ? totalUsed - effectiveLimit : 0;
          return {
            canUpload: false,
            reason: 'storage_limit',
            message: `Storage limit exceeded. You are using ${formatStorageSize(storageUsed)} of ${formatStorageSize(storageLimit)} (+${formatStorageSize(overflowAmount)} exceeded).${needed > 0 ? ` This upload would add ${formatStorageSize(needed)} more.` : ''} Please delete files or upgrade your plan to continue uploading.`,
            storageUsage,
          };
        }
        
        // If not in overflow yet, allow the upload (user can go slightly over limit)
        // The overflow state will be set by the backend trigger when limit is exceeded
        return {
          canUpload: true,
          storageUsage,
        };
      }
    } catch (error) {
      // If check fails, allow upload (don't block on error)
      // This prevents false positives when database is temporarily unavailable
      console.warn('Failed to check overflow/downgrade warning status, allowing upload:', error);
      return {
        canUpload: true,
        storageUsage,
      };
    }
    
    // Upload is within limits
    return {
      canUpload: true,
      storageUsage,
    };
  }

  /**
   * Get warning threshold (e.g., warn at 80% usage)
   */
  shouldWarnUser(storageUsage: StorageUsage): boolean {
    return storageUsage.percentage >= 80;
  }

  /**
   * Get the next recommended tier for upgrade
   */
  getRecommendedUpgrade(currentRole: UserRole, requiredStorage: number): UserRole | null {
    const roles = [UserRole.FREE, UserRole.STARTER, UserRole.PRO, UserRole.PREMIUM];
    const currentIndex = roles.indexOf(currentRole);
    
    for (let i = currentIndex + 1; i < roles.length; i++) {
      const tier = SUBSCRIPTION_TIERS[roles[i]];
      if (tier.storageLimit >= requiredStorage) {
        return roles[i];
      }
    }
    
    return null; // Already at max tier or need storage addon
  }
}

export const storageLimitService = new StorageLimitService();








